/**
 * Collapsible — Expandable/collapsible content section with animated transitions.
 *
 * Collapsible Component Guidelines
 * - Use Collapsible to progressively disclose secondary content (advanced settings,
 *   additional details, expandable sections). Prefer over conditional rendering
 *   (`{show && ...}`) when content should stay in the DOM for performance or when
 *   the expand/collapse animation is desired.
 * - Use the simple `<Collapsible>` with a `trigger` prop for standard expand/collapse.
 *   Switch to the composable API (`CollapsibleRoot` / `CollapsibleTrigger` /
 *   `CollapsibleContent`) when you need custom trigger placement or multiple content
 *   sections.
 * - Set `defaultOpen` to start expanded when the content is important enough that
 *   most users will want to see it immediately.
 * @see https://base-ui.com/react/components/collapsible Base UI Collapsible documentation
 * @see https://ui.shadcn.com/docs/components/collapsible shadcn/ui Collapsible documentation
 */
import React from "react";

import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible";

function CollapsibleRoot({ ...props }: CollapsiblePrimitive.Root.Props) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />;
}

function CollapsibleTrigger({ ...props }: CollapsiblePrimitive.Trigger.Props) {
  return (
    <CollapsiblePrimitive.Trigger data-slot="collapsible-trigger" {...props} />
  );
}

function CollapsibleContent({ ...props }: CollapsiblePrimitive.Panel.Props) {
  return (
    <CollapsiblePrimitive.Panel
      data-slot="collapsible-content"
      keepMounted
      className="h-[var(--collapsible-panel-height)] overflow-hidden transition-[height] duration-200 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 motion-reduce:transition-none"
      {...props}
    />
  );
}

type CollapsibleProps = React.PropsWithChildren<
  CollapsiblePrimitive.Root.Props & {
    trigger: React.ReactElement;
  }
>;

function Collapsible({ trigger, children, ...props }: CollapsibleProps) {
  return (
    <CollapsibleRoot {...props}>
      <CollapsibleTrigger render={trigger} />
      <CollapsibleContent>{children}</CollapsibleContent>
    </CollapsibleRoot>
  );
}

export { Collapsible, CollapsibleRoot, CollapsibleTrigger, CollapsibleContent };
